import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { QuestionCircleTwoTone } from '@ant-design/icons';
import { useMemoizedFn, useMount } from 'ahooks';
import {
  Affix,
  Divider,
  Input,
  PageHeader,
  Radio,
  Select,
  Space,
  Switch,
  Tooltip,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ArrowLeftIcon, EditIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { success } from 'components/RevxMessage';
import { RevxSpin } from 'components/RevxSpin';
import {
  Headline,
  SectionHeading,
  Text,
  TextHeavy,
  UnderlineText,
} from 'components/RevxTypography';
import { Equipment } from 'domains/eqptDomain';
import {
  InheritSettingEnum,
  InkFinishes,
  MultiTypeEnum,
  PartialSKU,
  SKU,
  SKUPart,
  SkuSize,
} from 'domains/skuDomain';
import { useSkuHideSaveAsNew } from 'hooks/useSkuHideSaveAsNew';
import { flattenDeep } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import skuService from 'services/skuService';
import {
  ADMIN_DETAIL_AFFIX_TOP,
  NAME_MAX_LENGTH,
  ALBERTSONS_ORGS,
} from 'utils/constants';
import { sleep } from 'utils/misc';
import { computeSkuDiffs, parcelCategoriesList } from 'utils/review';
import { DirectlyAddToCart } from './components/DirectlyAddToCart';
import { GalleryImage } from './components/GalleryImage';
import { GalleryLabel } from './components/GalleryLabel';
import { SkuDisplayName } from './components/SkuDisplayName';
import SingleSkuConfigWizard from './SingleSkuConfigWizard';
import './SingleSkuDetail.less';
import { SkuPublishReview } from './SkuPublishReview';
import { SpecifySuppliersFlag } from './components/SpecifySuppliers';
import { RevxDynamicTemplateModal } from 'components/RevxDynamicTemplateModal';

const SIZES_DEFAULT_VISIBLE_COUNT = 100;
type Props = {
  sku: PartialSKU;
  topSku?: PartialSKU;
  skuPart?: SKUPart;
  onCancel?: () => void;
};

const SingleSkuDetail = ({ sku, topSku, skuPart, onCancel }: Props) => {
  const { displaySize, display, unit } = useMeasurementUnit();
  const { t } = useTranslation();
  const curItem = sku;

  const dispatch = useAppDispatch();
  const [wizardVisible, setWizardVisible] = useState(false);
  const [publishVisible, setPublishVisible] = useState(false);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const skuTypes = useAppSelector((state) => state.app.skuTypes);
  const eqptTypes = useAppSelector((state) => state.eqpt.eqptTypes);
  const needSaveDraft = useAppSelector((state) => state.sku.needSaveDraft);
  const [editing, setEditing] = useState(false);
  const [exceptEqpts, setExceptEqpts] = useState<Equipment[]>([]);
  const [mainExceptEqpts, setMainExceptEqpts] = useState<Equipment[]>([]);
  const [mainSku, setMainSku] = useState<SKU>();
  const [inkFinishes, setInkFinishes] = useState<InkFinishes>();
  const orgId = useAppSelector((state) => state.app.orgId);

  const hideSaveAsNew = useSkuHideSaveAsNew(mainSku);

  /*const [paperCount, setPaperCount] = useState<number>(
    sku.defaultPaperCount || 0
  );*/
  const defaultSkuHandle = useMemoizedFn(async () => {
    const res = await dispatch.sku.getInk();
    setInkFinishes(res);
    document.documentElement.scrollTop = 0;
    const main = await skuService.startViewSku(curItem);
    dispatch.sku.setSingleStep(0);
    setMainSku(main);
  });
  useMount(() => {
    defaultSkuHandle();
  });

  /*const defaultPaperCount = useMemoizedFn(async () => {
    const allValues = {
      companies: sku.defaultPaperManufacturerName || [],
      categories: sku.defaultPaperCategory || [],
      basisWeights: sku.defaultPaperBasisWeight || [],
      brands: sku.defaultPaperName || [],
      colors: sku.defaultPaperColor || [],
      finishes: sku.defaultPaperFinish || [],
    };

    const count = await dispatch.sku.getPaperCount(allValues);
    setPaperCount(count);
  });*/

  useEffect(() => {
    if (!wizardVisible) {
      setDisplaySkuSizes(
        (curItem.skuSizes || [])
          .filter((item) => curItem.orientation?.includes(item.orientation))
          .slice(0, SIZES_DEFAULT_VISIBLE_COUNT)
      );

      // defaultPaperCount();
    }
  }, [wizardVisible, curItem.orientation, curItem.skuSizes]);

  const diffs = useMemo(() => {
    return computeSkuDiffs(
      sku,
      mainSku,
      eqptTypes,
      exceptEqpts,
      mainExceptEqpts,
      false,
      unit
    );
  }, [eqptTypes, exceptEqpts, mainExceptEqpts, mainSku, sku, unit]);

  const onClose = async () => {
    if (topSku) {
      let updatedTopSku;
      if (topSku.readonly) {
        updatedTopSku = topSku;
      } else {
        setEditing(true);
        updatedTopSku = await skuService.startEditSku(topSku);
        setEditing(false);
      }
      dispatch.sku.setState({
        curItem: updatedTopSku,
        curTopItem: undefined,
      });
    } else {
      dispatch.sku.setState({ curItem: undefined });
      dispatch.sku.refresh();
    }
  };

  const onSaveAsNew = useMemoizedFn(async () => {
    const draftSku = await skuService.createSku({
      isMulti: Boolean(mainSku?.multiType === MultiTypeEnum.WRAPPER),
      templateId: mainSku?.id,
    });

    dispatch.sku.setState({
      curItem: draftSku,
    });
    dispatch.sku.refresh();

    success(t('platform.sku.createSuccess'));

    // defaultSkuHandle();
    // onClose();
  });

  const updateCurItem = async (item: PartialSKU) => {
    try {
      await dispatch.sku.updateCurItemForSave(item);
      success(t('platform.sku.changesSavedTip'));
    } catch {}
  };
  const updateCurItemForLaterSave = async (item: PartialSKU) => {
    try {
      await dispatch.sku.updateCurItemForLaterSave(item);
    } catch {}
  };

  const parcelCategoriesError = useMemo(() => {
    return Boolean(
      (!sku?.parcelCategories || !sku?.parcelCategories?.length) &&
        !curItem?.readonly
    );
  }, [curItem, sku]);

  const galleryImageError = useMemo(() => {
    return Boolean(
      !curItem.images?.[0] &&
        !curItem.readonly &&
        !!curItem.active &&
        !topSku &&
        curItem.multiType !== MultiTypeEnum.PART
    );
  }, [curItem, topSku]);

  const showConfigTemplates = useMemo(() => {
    return (
      curItem?.companyLabel?.id && orgId && ALBERTSONS_ORGS.includes(orgId)
    );
  }, [curItem, orgId]);

  const topGalleryLabelError = useMemo(() => {
    if (!curItem.isBaseSku) return false;
    return (
      (!curItem.topGalleryLabels || !curItem.topGalleryLabels.length) &&
      curItem.multiType !== MultiTypeEnum.PART &&
      !curItem.readonly &&
      !!curItem.active
    );
  }, [curItem]);

  const galleryLabelError = useMemo(() => {
    if (!curItem.isBaseSku) return false;
    return (
      !curItem?.galleryLabel?.id &&
      curItem.multiType !== MultiTypeEnum.PART &&
      !curItem.readonly &&
      !!curItem.active
    );
  }, [curItem]);

  const companyLabelError = useMemo(() => {
    return (
      !curItem?.companyLabel?.id &&
      curItem.multiType !== MultiTypeEnum.PART &&
      !curItem.readonly &&
      !!curItem.active
    );
  }, [curItem]);

  const reviewChangesDisabled = useMemo(() => {
    return Boolean(
      diffs.length === 0 ||
        galleryImageError ||
        topGalleryLabelError ||
        galleryLabelError ||
        parcelCategoriesError
    );
  }, [
    diffs,
    galleryImageError,
    topGalleryLabelError,
    galleryLabelError,
    parcelCategoriesError,
  ]);

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            onClose();
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <div style={{ display: 'flex' }}>
              {topSku && (
                <Headline key="1" style={{ width: 'auto' }}>
                  <Tooltip title={`[${topSku.name}] ${curItem.name}`}>
                    [{topSku.name}]
                  </Tooltip>
                </Headline>
              )}
              {!curItem.readonly && !topSku && (
                <Headline key="1" style={{ width: 'auto' }}>
                  [{t('platform.sku.draft')}]
                </Headline>
              )}
              <Headline
                ellipsis={{
                  tooltip: topSku
                    ? `[${topSku?.name}] ${curItem.name}`
                    : curItem.name,
                }}
                style={{ marginTop: 0, minWidth: 400, marginLeft: 10 }}
                key="2"
                editable={
                  curItem.readonly
                    ? undefined
                    : {
                        ...(curItem.name ? {} : { editing: true }),
                        maxLength: NAME_MAX_LENGTH,
                        onStart: () => {
                          updateCurItemForLaterSave({
                            ...curItem,
                          });
                        },
                        onChange: (value) => {
                          if (value !== curItem.name) {
                            updateCurItem({
                              ...curItem,
                              name: value,
                            });
                          } else {
                            dispatch.sku.setState({
                              needSaveDraft: false,
                            });
                          }
                        },
                        icon: <EditIcon />,
                      }
                }
              >
                {curItem.name}
              </Headline>
            </div>
          }
          extra={
            curItem.readonly
              ? [
                  <RevxButton
                    onClick={onSaveAsNew}
                    brandGreen
                    type="primary"
                    key="onSaveAsNew"
                    hidden={hideSaveAsNew}
                  >
                    {t('common.saveAsNew')}
                  </RevxButton>,
                  <RevxButton onClick={onClose} brandGreen key="cancel">
                    {t('common.cancel')}
                  </RevxButton>,
                ]
              : [
                  <RevxButton
                    type="primary"
                    htmlType="submit"
                    key="publish"
                    disabled={reviewChangesDisabled}
                    onClick={() => {
                      setPublishVisible(true);
                    }}
                    brandGreen
                  >
                    <Tooltip
                      title={
                        diffs.length > 0
                          ? ''
                          : t('platform.sku.publishReview.onChangesFound')
                      }
                    >
                      {topSku
                        ? t(
                            'platform.sku.publishReview.reviewChangesIndividual'
                          )
                        : t('platform.sku.publishReview.reviewChanges')}
                    </Tooltip>
                  </RevxButton>,
                ]
          }
        />
      </Affix>
    </div>
  );

  const [displaySkuSizes, setDisplaySkuSizes] = useState<SkuSize[]>(
    (curItem.skuSizes || [])
      .filter((item) => curItem.orientation?.includes(item.orientation))
      .slice(0, SIZES_DEFAULT_VISIBLE_COUNT)
  );

  const inkRender = (
    type: 'finishes' | 'ink',
    str: string[] | undefined
  ): string => {
    if (type === 'finishes') {
      return (str || [])
        .filter((item) => item !== 'none')
        .map(
          (item) =>
            inkFinishes?.finishes?.filter(
              (finishes) => finishes?.value === item
            )[0]?.label
        )
        .join(', ');
    } else if (type === 'ink') {
      return (str || [])
        .filter((item) => item !== 'none')
        .map(
          (item) =>
            inkFinishes?.inks?.filter((finishes) => finishes?.value === item)[0]
              ?.label
        )
        .join(', ');
    } else {
      return '';
    }
  };

  useEffect(() => {
    const updateExceptEqpts = async () => {
      if (mainSku) {
        const [exceptEqpts, mainExceptEqpts] = await Promise.all([
          skuService.getExceptEqpts(curItem),
          skuService.getExceptEqpts(mainSku),
        ]);
        setExceptEqpts(exceptEqpts);
        setMainExceptEqpts(mainExceptEqpts);
      } else {
        const exceptEqpts = await skuService.getExceptEqpts(curItem);
        setExceptEqpts(exceptEqpts);
      }
    };
    updateExceptEqpts();
  }, [curItem, mainSku]);

  return (
    <RevxSpin spinning={editing}>
      <div
        className="single-sku-detail"
        hidden={wizardVisible || publishVisible}
      >
        {actionBar}
        <div className="single-sku-detail-form">
          <div className="single-sku-detail-form-row">
            <Space>
              <label>{t('platform.sku.form.label.active')}:</label>
              <Switch
                checked={curItem.active}
                disabled={curItem.readonly}
                onChange={(val) => {
                  updateCurItem({
                    ...curItem,
                    active: val,
                  });
                }}
              />
              <Divider type="vertical" />
              <label>{t('platform.sku.form.label.skuType')}:</label>
              <Select
                style={{ width: 200 }}
                showSearch
                disabled={curItem.readonly || !!curItem.skuType}
                value={curItem?.skuType?.id}
                filterOption={(input, option) =>
                  option?.children
                    ?.toLowerCase()
                    ?.indexOf(input?.toLowerCase()) >= 0
                }
                onChange={(val) => {
                  updateCurItem({
                    ...curItem,
                    skuType: skuTypes.find((t) => t.id === val),
                  });
                }}
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
              >
                {skuTypes.map((skuType) => {
                  return (
                    <Select.Option value={skuType.id} key={skuType.id}>
                      {skuType.name}
                    </Select.Option>
                  );
                })}
              </Select>
              <div
                hidden={!!curItem.skuType || curItem.readonly}
                className="ant-form-item-explain-error"
                style={{
                  position: 'absolute',
                  left: 204,
                  top: 35,
                }}
              >
                {t('platform.sku.skuTypeRequired')}
              </div>
              <Divider type="vertical" />
              <label>{t('platform.sku.form.label.taxCode')}:</label>
              <Input
                disabled={curItem.readonly}
                value={curItem.taxCode}
                onChange={(e) => {
                  if (e.target.value !== curItem.taxCode) {
                    updateCurItemForLaterSave({
                      ...curItem,
                      taxCode: e.target.value,
                    });
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value !== curItem.taxCode || needSaveDraft) {
                    updateCurItem({
                      ...curItem,
                      taxCode: e.target.value,
                    });
                  }
                }}
              />
              <a
                href="https://taxcode.avatax.avalara.com/"
                target="_blank"
                rel="noreferrer"
              >
                <QuestionCircleTwoTone />
              </a>
              <Divider type="vertical" />
              <label>{t('platform.sku.form.label.parcelCategories')}:</label>
              <div style={{ position: 'relative' }}>
                <Select
                  mode="multiple"
                  style={{ minWidth: 200 }}
                  showSearch
                  disabled={sku.readonly}
                  value={sku?.parcelCategories || undefined}
                  filterOption={(input, option) =>
                    Boolean(
                      (parcelCategoriesList || [])
                        .find((item) => item?.value === option?.value)
                        ?.title?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    )
                  }
                  onChange={(val) => {
                    updateCurItem({
                      ...sku,
                      parcelCategories: val,
                    });
                  }}
                  getPopupContainer={(e) => {
                    return e as HTMLElement;
                  }}
                  dropdownStyle={{
                    zIndex: 4,
                  }}
                >
                  {parcelCategoriesList.map((parcelCategorie) => {
                    return (
                      <Select.Option
                        value={parcelCategorie.value}
                        key={parcelCategorie.value}
                      >
                        <span>{parcelCategorie.title}</span>
                      </Select.Option>
                    );
                  })}
                </Select>
                <div
                  hidden={!parcelCategoriesError}
                  className="ant-form-item-explain-error"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 35,
                  }}
                >
                  {t('platform.sku.parcelCategoriesRequired')}
                </div>
              </div>
            </Space>
          </div>
          <GalleryLabel
            showTopGalleryLabelsError={topGalleryLabelError}
            showGalleryLabelError={galleryLabelError}
            showCompanyLabelError={companyLabelError}
            sku={curItem}
            updateCurItem={updateCurItem}
          />
          <SkuDisplayName sku={curItem} updateCurItem={updateCurItem} />
          <RevxFlexLayout
            style={{ marginTop: 20 }}
            justify="space-between"
            align="top"
          >
            <GalleryImage sku={curItem} showError={galleryImageError} />

            <div className="single-sku-config-options">
              <Headline style={{ marginBottom: 20 }}>
                <RevxFlexLayout>
                  <span>{t('platform.sku.form.label.configOptions')}</span>
                  <RevxButton
                    brandGreen
                    type="primary"
                    size="small"
                    style={{ marginLeft: 20 }}
                    onClick={() => setWizardVisible(true)}
                    disabled={!Boolean(curItem?.skuType?.id)}
                  >
                    {t('platform.sku.form.label.goNow')}
                  </RevxButton>
                </RevxFlexLayout>
                {showConfigTemplates && (
                  <RevxFlexLayout>
                    <span>{t('platform.sku.form.label.configTemplates')}</span>
                    <RevxButton
                      brandGreen
                      type="primary"
                      size="small"
                      style={{ marginLeft: 20 }}
                      onClick={() => setTemplateModalVisible(true)}
                      disabled={!Boolean(curItem?.skuType?.id)}
                    >
                      {t('platform.sku.form.label.goNow')}
                    </RevxButton>
                  </RevxFlexLayout>
                )}
              </Headline>
              <div className="single-sku-detail-form-row">
                <label>{t('platform.sku.form.label.skuUse')}:</label>
                <Radio.Group
                  value={curItem?.multiType}
                  disabled={curItem.readonly}
                  onChange={(e) => {
                    updateCurItem({
                      ...curItem,
                      multiType: e.target.value as MultiTypeEnum,
                    });
                  }}
                >
                  {!topSku && (
                    <Radio value={MultiTypeEnum.STANDALONE}>
                      {t('platform.sku.form.label.standaloneSku')}
                    </Radio>
                  )}
                  <Radio value={MultiTypeEnum.PART}>
                    {t('platform.sku.form.label.includeWithMultiSku')}
                  </Radio>
                  <Radio value={MultiTypeEnum.BOTH}>
                    {t('platform.sku.form.label.both')}
                  </Radio>
                </Radio.Group>
              </div>
              <div
                className="single-sku-detail-form-row"
                hidden={curItem?.isBaseSku}
              >
                <label>{t('platform.sku.form.label.inheritUpdates')}:</label>
                <Radio.Group
                  value={curItem?.inheritSetting}
                  disabled={curItem.readonly}
                  onChange={(e) => {
                    updateCurItem({
                      ...curItem,
                      inheritSetting: e.target.value as InheritSettingEnum,
                    });
                  }}
                >
                  <Radio value={InheritSettingEnum.EXCEPTION_AUTO}>
                    {t('platform.sku.form.label.exceptionAuto')}
                  </Radio>
                  <Radio value={InheritSettingEnum.EXCEPTION_MANUAL}>
                    {t('platform.sku.form.label.exceptionManual')}
                  </Radio>
                  <Radio value={InheritSettingEnum.NO_EXCEPTION}>
                    {t('platform.sku.form.label.noException')}
                  </Radio>
                </Radio.Group>
              </div>
              <div
                className="single-sku-detail-form-row"
                hidden={curItem?.multiType === MultiTypeEnum.PART}
              >
                <label>{t('platform.sku.form.label.hideFreightCost')}</label>
                <Radio.Group
                  value={Boolean(curItem?.hideFreightCost)}
                  disabled={curItem.readonly}
                  onChange={(e) => {
                    updateCurItem({
                      ...curItem,
                      hideFreightCost: e.target.value,
                    });
                  }}
                >
                  <Radio value={true}>{t('common.yes')}</Radio>
                  <Radio value={false}>{t('common.no')}</Radio>
                </Radio.Group>
              </div>
              <DirectlyAddToCart
                curItem={curItem}
                updateCurItem={updateCurItem}
              />
              <div className="single-sku-detail-form-row">
                <Space>
                  <span>{t('platform.sku.form.tip.thisImpacts')}</span>
                  <UnderlineText color="blue">10 multi-SKUs</UnderlineText>,
                  <UnderlineText color="blue">1 base SKU</UnderlineText>,
                  <UnderlineText color="blue">10 org SKUs</UnderlineText>
                </Space>
              </div>
              <div className="single-sku-overview">
                <div className="single-sku-overview-item">
                  <div className="single-sku-overview-item-label">
                    {t('platform.sku.publishReview.optionTitle.qtyRules')}
                  </div>
                  <div className="single-sku-overview-item-content">
                    <ul>
                      <li>
                        <strong>
                          {t(
                            'platform.sku.publishReview.optionTitle.defaultQuantity'
                          )}
                          :
                        </strong>{' '}
                        <span>{curItem?.defaultQty}</span>
                      </li>
                      <li>
                        <strong>
                          {t(
                            'platform.sku.publishReview.optionTitle.defaultSides'
                          )}
                          :
                        </strong>{' '}
                        <span>{curItem?.sides}</span>
                      </li>
                      <li>
                        <strong>
                          {t(
                            'platform.sku.publishReview.optionTitle.defaultPageCount'
                          )}
                          :
                        </strong>{' '}
                        <span>{curItem?.defaultPageCount}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="single-sku-overview-item">
                  <div className="single-sku-overview-item-label">
                    {t('platform.sku.form.label.sizes')}
                  </div>
                  <div className="single-sku-overview-item-content">
                    {!!curItem.customRuleCode && (
                      <span>
                        <TextHeavy>
                          {t(
                            'platform.sku.publishReview.optionTitle.customRuleCode'
                          )}
                          :{' '}
                        </TextHeavy>
                        <Text>{curItem.customRuleCode}</Text>
                      </span>
                    )}
                    <ul>
                      <li>
                        <strong>
                          {t(
                            'platform.sku.publishReview.optionTitle.orientations'
                          )}
                          :
                        </strong>{' '}
                        <span>{(curItem?.orientation || []).join(', ')}</span>
                      </li>
                      {displaySkuSizes.map((sizeItem, index) => {
                        return (
                          <li key={index}>
                            <Space>
                              <span>
                                <TextHeavy>
                                  {t(
                                    'platform.sku.publishReview.optionTitle.finishedSize'
                                  )}
                                  :{' '}
                                </TextHeavy>
                                <Text>{displaySize(sizeItem.finishedSize, { metric: sizeItem.finishedSizeMetric })}</Text> {',   '}
                              </span>
                              <span>
                                <TextHeavy>
                                  {t(
                                    'platform.sku.publishReview.optionTitle.flatSize'
                                  )}
                                  :{' '}
                                </TextHeavy>
                                <Text>{displaySize(sizeItem.flatSize, { metric: sizeItem.flatSizeMetric })}</Text>
                                {',   '}
                              </span>
                              <span>
                                <TextHeavy>
                                  {t(
                                    'platform.sku.publishReview.optionTitle.orientation'
                                  )}
                                  :{' '}
                                </TextHeavy>
                                <Text>{sizeItem.orientation}</Text>
                              </span>
                            </Space>
                          </li>
                        );
                      })}
                    </ul>
                    {(curItem.skuSizes || []).filter((item) =>
                      curItem.orientation?.includes(item.orientation)
                    ).length > SIZES_DEFAULT_VISIBLE_COUNT && (
                      <div>
                        <UnderlineText
                          color="blue"
                          onClick={() => {
                            const skuSizes = (curItem.skuSizes || []).filter(
                              (item) =>
                                curItem.orientation?.includes(item.orientation)
                            );
                            if (displaySkuSizes.length >= skuSizes.length) {
                              setDisplaySkuSizes(
                                skuSizes.slice(0, SIZES_DEFAULT_VISIBLE_COUNT)
                              );
                            } else {
                              setDisplaySkuSizes(skuSizes);
                            }
                          }}
                        >
                          {displaySkuSizes.length >=
                          (curItem.skuSizes || []).filter((item) =>
                            curItem.orientation?.includes(item.orientation)
                          ).length
                            ? t('platform.sku.form.btn.viewLessSizes')
                            : t('platform.sku.form.btn.viewAllSizes')}
                        </UnderlineText>
                      </div>
                    )}

                    {curItem.customSizeEnable && (
                      <div>
                        <Text>
                          {t('platform.sku.form.tip.customSizesAvailable')}
                        </Text>
                      </div>
                    )}

                    {!!curItem.defaultBleedSizeIn && (
                      <span>
                        <TextHeavy>
                          {t('platform.sku.publishReview.optionTitle.bleed')}:{' '}
                        </TextHeavy>
                        <Text>
                        {display(curItem.defaultBleedSizeIn, {
                          metric: curItem.defaultBleedSizeMm,
                        })}
                      </Text>
                      </span>
                    )}
                  </div>
                </div>
                <div className="single-sku-overview-item">
                  <div className="single-sku-overview-item-label">
                    {t('platform.sku.form.label.paperRules')}
                  </div>
                  <div className="single-sku-overview-item-content">
                    <ul>
                      <li>
                        <strong>
                          {t('platform.sku.wizard.paper.form.label.category')}:
                        </strong>{' '}
                        <span>
                          {(
                            curItem?.skuPaperOptions?.optionCombs.map(
                              (i) => i.categoryOption
                            ) || []
                          ).join(', ')}
                        </span>
                      </li>
                      <li>
                        <strong>
                          {t(
                            'platform.sku.wizard.paper.form.label.basisWeight'
                          )}
                          :
                        </strong>{' '}
                        <span>
                          {(
                            curItem?.skuPaperOptions?.optionCombs.map(
                              (i) => i.basisWeightOptions
                            ) || []
                          ).join(', ')}
                        </span>
                      </li>
                      <li>
                        <strong>
                          {t('platform.sku.wizard.paper.form.label.color')}:
                        </strong>{' '}
                        <span>
                          {(curItem?.skuPaperOptions?.colorOptions || []).join(
                            ', '
                          )}
                        </span>
                      </li>
                      <li>
                        <strong>
                          {t('platform.sku.wizard.paper.form.label.finish')}:
                        </strong>{' '}
                        <span>
                          {(() => {
                            let t: string[][] = [];
                            curItem?.skuPaperOptions?.optionCombs.forEach((i) =>
                              t.push(i.finishOptions)
                            );
                            return flattenDeep(t)
                              .map((i) => i)
                              .join(', ');
                          })()}
                        </span>
                      </li>
                    </ul>

                    {/* <div>
                      {curItem?.defaultPaperCount}{' '}
                      <Text>{t('platform.sku.form.label.papers')}</Text>
                    </div> */}

                    {/*<div>
                      <Text>
                        {t('platform.sku.wizard.paper.form.label.footerTip', {
                          paperNumber: paperCount,
                        })}
                      </Text>
                    </div>*/}
                  </div>
                </div>
                <div className="single-sku-overview-item">
                  <div className="single-sku-overview-item-label">
                    {t('platform.sku.form.label.press')}
                  </div>
                  <div className="single-sku-overview-item-content">
                    <div className="bold">
                      <SpecifySuppliersFlag sku={sku} />
                    </div>
                    {curItem?.equipmentCodes?.length === eqptTypes.length &&
                    curItem?.exceptEquipmentIds?.length === 0 ? (
                      <Text>
                        {t('platform.sku.form.tip.noPressExceptions')}
                      </Text>
                    ) : (
                      <>
                        <ul>
                          {(curItem.equipmentCodes || []).map((eqptCode) => {
                            const eqptType = eqptTypes.find(
                              (item) => item.code === eqptCode
                            );
                            return (
                              <li key={eqptCode}>
                                {eqptType?.name} (
                                <TextHeavy>{eqptType?.code}</TextHeavy>)
                              </li>
                            );
                          })}
                        </ul>
                        {exceptEqpts.length > 0 && (
                          <div>
                            <SectionHeading>
                              {t('platform.sku.exceptions')}:
                            </SectionHeading>
                            <ul>
                              {exceptEqpts.map((eqpt) => {
                                return <li key={eqpt.id}>{eqpt.name}</li>;
                              })}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="single-sku-overview-item">
                  <div className="single-sku-overview-item-label">
                    {t('platform.sku.form.label.ink')}
                  </div>
                  <div className="single-sku-overview-item-content">
                    <div>
                      <TextHeavy>
                        {t(
                          'platform.sku.publishReview.optionTitle.coatingFront'
                        )}
                        :{' '}
                      </TextHeavy>
                      <Text>
                        {inkRender('finishes', curItem?.coatingFront)}
                      </Text>
                    </div>{' '}
                    <div>
                      <TextHeavy>
                        {t(
                          'platform.sku.publishReview.optionTitle.coatingBack'
                        )}
                        :{' '}
                      </TextHeavy>
                      <Text>{inkRender('finishes', curItem?.coatingBack)}</Text>
                    </div>{' '}
                    <div>
                      <TextHeavy>
                        {t('platform.sku.publishReview.optionTitle.inkFront')}:{' '}
                      </TextHeavy>
                      <Text>{inkRender('ink', curItem?.inkFront)}</Text>
                    </div>
                    <div>
                      <TextHeavy>
                        {t('platform.sku.publishReview.optionTitle.inkBack')}:{' '}
                      </TextHeavy>
                      <Text>{inkRender('ink', curItem?.inkBack)}</Text>
                    </div>
                    <div>
                      <TextHeavy>
                        {t(
                          'platform.sku.publishReview.optionTitle.hasPmsFront'
                        )}
                        :{' '}
                      </TextHeavy>
                      <Text>{String(curItem?.hasPmsFront)}</Text>
                    </div>
                    <div>
                      <TextHeavy>
                        {t('platform.sku.publishReview.optionTitle.hasPmsBack')}
                        :{' '}
                      </TextHeavy>
                      <Text>{String(curItem?.hasPmsBack)}</Text>
                    </div>
                  </div>
                </div>
                <div className="single-sku-overview-item">
                  <div className="single-sku-overview-item-label">
                    {t('platform.sku.form.label.requiredServices')}
                  </div>
                  <div className="single-sku-overview-item-content">
                    {(curItem?.requiredSvcDetails || []).length > 0 && (
                      <>
                        <SectionHeading>
                          {t('platform.sku.serviceTypes')}:
                        </SectionHeading>
                        <ul>
                          {(curItem?.requiredSvcDetails || []).map(
                            (s, index: number) => {
                              return (
                                <li key={index}>
                                  <TextHeavy>{s.name}</TextHeavy>
                                </li>
                              );
                            }
                          )}
                          {(curItem?.exceptRequiredSvcs || []).length > 0 && (
                            <li>
                              <SectionHeading style={{ marginTop: 10 }}>
                                {t('platform.sku.exceptions')}:
                              </SectionHeading>
                              <ul>
                                {(curItem?.exceptRequiredSvcs || []).map(
                                  (svc) => {
                                    return <li key={svc.id}>{svc.name}</li>;
                                  }
                                )}
                              </ul>
                            </li>
                          )}
                        </ul>
                      </>
                    )}

                    {(curItem?.requiredSvcs || []).length > 0 && (
                      <>
                        <SectionHeading style={{ marginTop: 10 }}>
                          {t('platform.sku.services')}:
                        </SectionHeading>
                        <ul>
                          {(curItem?.requiredSvcs || []).map(
                            (s, index: number) => {
                              return (
                                <li key={index}>
                                  <TextHeavy>{s.name}</TextHeavy>
                                </li>
                              );
                            }
                          )}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
                <div className="single-sku-overview-item">
                  <div className="single-sku-overview-item-label">
                    {t('platform.sku.form.label.optionalServices')}
                  </div>
                  <div className="single-sku-overview-item-content">
                    <SectionHeading>
                      {t('platform.sku.serviceTypes')}:
                    </SectionHeading>
                    <ul>
                      {(curItem?.optionalSvcDetails || []).map(
                        (s, index: number) => {
                          return (
                            <li key={index}>
                              <TextHeavy>{s.name}</TextHeavy>
                            </li>
                          );
                        }
                      )}
                    </ul>
                  </div>
                </div>
                <div className="single-sku-overview-item">
                  <div className="single-sku-overview-item-label">
                    {t('platform.sku.form.label.jobOptionDetails')}
                  </div>
                  <div className="single-sku-overview-item-content">
                    <SectionHeading>
                      {t('platform.sku.serviceTypes')}:
                    </SectionHeading>
                    <ul>
                      {(curItem?.jobOptionDetails || []).map(
                        (s, index: number) => {
                          return (
                            <li key={index}>
                              <TextHeavy>{s.name}</TextHeavy>
                            </li>
                          );
                        }
                      )}
                    </ul>
                  </div>
                </div>
                <div className="single-sku-overview-item">
                  <div className="single-sku-overview-item-label">
                    {t('platform.sku.form.label.additionalJobOptionDetails')}
                  </div>
                  <div className="single-sku-overview-item-content">
                    <SectionHeading>
                      {t('platform.sku.serviceTypes')}:
                    </SectionHeading>
                    <ul>
                      {(curItem?.additionalJobOptionDetails || []).map(
                        (s, index: number) => {
                          return (
                            <li key={index}>
                              <TextHeavy>{s.name}</TextHeavy>
                            </li>
                          );
                        }
                      )}
                    </ul>
                  </div>
                </div>
                <div className="single-sku-overview-item">
                  <div className="single-sku-overview-item-label">
                    {t('platform.sku.form.label.extraSpecValues')}
                  </div>
                  <div className="single-sku-overview-item-content">
                    {curItem.extraSpecs ? (
                      <ul>
                        {curItem.extraSpecs.map((spec, index) => {
                          return (
                            <li key={index}>
                              <TextHeavy>{Object.keys(spec)[0]}: </TextHeavy>{' '}
                              {Object.values(spec)[0]}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <div>
                        <Text>
                          {t('platform.sku.form.tip.noAdditionalFields')}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </RevxFlexLayout>
        </div>
      </div>
      {wizardVisible && (
        <SingleSkuConfigWizard
          sku={curItem}
          topSku={topSku}
          skuPart={skuPart}
          onCancel={() => {
            setWizardVisible(false);
          }}
        />
      )}
      {publishVisible && mainSku && (
        <SkuPublishReview
          hidePublishBtn={!!topSku}
          exceptEqpts={exceptEqpts}
          mainExceptEqpts={mainExceptEqpts}
          eqptTypes={eqptTypes}
          sku={curItem}
          mainSku={mainSku}
          isMulti={false}
          partDiffMap={{}}
          onClose={async () => {
            setPublishVisible(false);
            await sleep(3000);
            const main = await skuService.startViewSku(curItem);
            setMainSku(main);
          }}
        />
      )}
      <RevxDynamicTemplateModal
        visible={templateModalVisible}
        skuId={mainSku?.id}
        onCancel={() => setTemplateModalVisible(false)}
        onConfirm={(frontTemplate, backTemplate) => {
          console.log('Selected templates:', { frontTemplate, backTemplate });
          // TODO: 处理选中的模板
          setTemplateModalVisible(false);
        }}
      />
    </RevxSpin>
  );
};
export default SingleSkuDetail;
